import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { ArrowLeft } from 'lucide-react';
import useLegalConfig from '@/hooks/useLegalConfig';

function Section({ title, children }) {
  return (
    <section className="mb-8">
      <h2 className="text-2xl font-semibold mb-4">{title}</h2>
      <div className="text-slate-600 leading-relaxed space-y-4">{children}</div>
    </section>
  );
}

function List({ children }) {
  return <ul className="list-disc list-inside space-y-2 ml-4">{children}</ul>;
}

function Header({ config, document }) {
  const versionKey = `${document}_version`;
  return (
    <>
      {!config.commercial_ready && (
        <Alert variant="destructive" className="mb-8">
          <AlertDescription>
            This environment is not configured for commercial use. The legal operator or production infrastructure details are incomplete.
          </AlertDescription>
        </Alert>
      )}
      <div className="mb-8 text-sm text-slate-500">
        Operator: <strong>{config.company_name}</strong><br />
        Address: {config.company_address}<br />
        Contact: {config.contact_email || 'Not configured'}<br />
        Version: {config[versionKey]} · Effective: {config.effective_date}
      </div>
    </>
  );
}

function PrivacyContent({ config }) {
  return (
    <>
      <Section title="1. Who is responsible for the data">
        <p>{config.company_name} operates EVA AI. For account administration, billing metadata and the operation of the EVA AI service, the operator may act as data controller. When a customer studio uses EVA AI to communicate with its own WhatsApp contacts, the studio generally determines the purpose of that customer communication and EVA AI processes that data to provide the service under the Data Processing Agreement.</p>
        <p>Privacy requests can be sent to <strong>{config.contact_email || 'the contact address configured by the operator'}</strong>.</p>
      </Section>
      <Section title="2. Data EVA AI may process">
        <List>
          <li>Account data such as email address, studio name, authentication and verification status.</li>
          <li>WhatsApp identifiers, message content, conversation state and booking information required to operate the assistant.</li>
          <li>Service and security metadata such as timestamps, delivery status, audit events and technical error information.</li>
          <li>Subscription and billing references. Card details are handled by Stripe and are not stored by EVA AI.</li>
          <li>Configuration supplied by the studio, including public business information and website-derived knowledge when that feature is used.</li>
        </List>
      </Section>
      <Section title="3. Why data is processed">
        <p>Data is processed to provide and secure the service, authenticate users, deliver and reconcile WhatsApp messages, manage bookings, operate subscriptions, support account recovery, diagnose failures and comply with applicable legal obligations. The exact legal basis depends on the role of the parties and the applicable law.</p>
      </Section>
      <Section title="4. Service providers and processing locations">
        <p>EVA AI relies on third-party services needed to operate the product. Depending on the configured production route, these may include Meta/WhatsApp for messaging, Stripe for billing, MongoDB Atlas for database storage, Anthropic and/or OpenAI for language-model processing, Resend for transactional account email, and <strong>{config.hosting_provider}</strong> for application hosting.</p>
        <p>Configured production processing-location information: <strong>{config.processing_locations}</strong>. The operator must keep this information aligned with the actual deployed infrastructure and provider arrangements.</p>
      </Section>
      <Section title="5. International processing">
        <p>Some providers may process data outside Switzerland or the country of the customer. Where cross-border safeguards are legally required, the operator and/or relevant provider arrangements must supply the required transfer mechanism. This policy does not claim a specific transfer mechanism unless it is actually configured and documented for production.</p>
      </Section>
      <Section title="6. Retention and deletion">
        <p>EVA AI does not publish a blanket automatic 90-day deletion promise. Operational data is retained only for as long as needed for the service, security, dispute handling or applicable legal obligations, with different categories and backups potentially following different lifecycles. Until a self-service export/deletion workflow is available, verified requests are handled through the contact address above.</p>
      </Section>
      <Section title="7. Security">
        <p>EVA AI uses access controls, tenant scoping, password hashing, JWT/session revocation controls, webhook signature checks, security logging and authenticated application-level encryption for stored tenant WhatsApp access tokens. Network and infrastructure protections also depend on the configured hosting, database and third-party providers. No online service can guarantee absolute security.</p>
      </Section>
      <Section title="8. Your rights">
        <p>Depending on the applicable law and the role of the requesting party, rights may include access, correction, deletion, restriction, objection or data portability. A studio remains responsible for handling requests from its own WhatsApp customers when it acts as controller; EVA AI will provide reasonable processor assistance under the DPA.</p>
      </Section>
      <Section title="9. Changes and contact">
        <p>The version and effective date shown at the top identify the policy presented by EVA AI. Material updates may require updated notice or acceptance depending on the change and applicable law.</p>
        <p>Contact: <strong>{config.contact_email || 'Not configured'}</strong> · {config.company_address}</p>
      </Section>
    </>
  );
}

function TermsContent({ config }) {
  return (
    <>
      <Section title="1. Service and operator">
        <p>These Terms govern use of EVA AI, a software service operated by <strong>{config.company_name}</strong>. EVA AI provides WhatsApp-assisted customer conversations, booking workflows, account tools and related administration features.</p>
      </Section>
      <Section title="2. Subscription and billing">
        <p>The configured standard subscription price is <strong>{config.monthly_price_chf} CHF per month</strong>. Billing is processed through Stripe. A subscription may be cancelled from the supported account flow and cancellation is scheduled for the end of the current paid period. Fees already charged for an active period are not automatically refundable except where required by law or separately agreed.</p>
      </Section>
      <Section title="3. Customer responsibilities">
        <List>
          <li>Keep EVA AI, Meta/WhatsApp and other provider credentials secure and current.</li>
          <li>Use the service lawfully and comply with Meta/WhatsApp rules applicable to the customer's account and messaging.</li>
          <li>Provide accurate studio information, booking rules and customer notices.</li>
          <li>Ensure an appropriate legal basis and transparency for communications with the studio's own customers.</li>
          <li>Review important configurations and operational exceptions rather than treating AI-generated output as infallible.</li>
        </List>
      </Section>
      <Section title="4. Automated service limitations">
        <p>EVA AI uses deterministic booking controls together with automated language-model processing. Automated interpretation can still be wrong or unavailable. A booking should be treated as confirmed only when the service records and communicates a confirmed booking state. EVA AI is not a substitute for professional legal, financial, medical or safety advice.</p>
      </Section>
      <Section title="5. Availability and third-party dependencies">
        <p>No uptime SLA is promised by these Terms unless a separate written agreement says otherwise. Availability can be affected by Meta/WhatsApp, Stripe, database, hosting, email, LLM providers, internet connectivity and scheduled or emergency maintenance.</p>
      </Section>
      <Section title="6. Acceptable use">
        <p>The service must not be used for unlawful activity, fraud, impersonation, spam, abusive conduct, unauthorized access, attempts to bypass security controls, or infringement of third-party rights.</p>
      </Section>
      <Section title="7. Data protection">
        <p>The Privacy Policy explains account and service data practices. Where EVA AI processes personal data on behalf of a customer studio, the Data Processing Agreement forms part of the service terms.</p>
      </Section>
      <Section title="8. Suspension and termination">
        <p>Access may be restricted or suspended when reasonably necessary for security, unlawful use, serious policy violations, provider restrictions or non-payment. Termination and any post-termination data handling remain subject to the applicable contract, DPA and mandatory law.</p>
      </Section>
      <Section title="9. Intellectual property">
        <p>EVA AI software, branding and service materials remain the property of the operator or its licensors. The customer receives a limited right to use the service for the duration of the subscription, subject to these Terms.</p>
      </Section>
      <Section title="10. Liability">
        <p>Liability is governed by these Terms together with mandatory applicable law. Nothing here excludes liability that cannot legally be excluded. EVA AI does not assume responsibility for failures caused solely by third-party services outside its reasonable control or for customer configurations and content outside the operator's control.</p>
      </Section>
      <Section title="11. Governing law and contact">
        <p>The configured jurisdiction is <strong>{config.jurisdiction}</strong>, subject to any mandatory consumer, venue or other rules that apply. Questions can be sent to <strong>{config.contact_email || 'Not configured'}</strong>.</p>
      </Section>
    </>
  );
}

function DpaContent({ config }) {
  return (
    <>
      <Section title="1. Roles and scope">
        <p>For personal data that a customer studio submits to EVA AI for WhatsApp conversations and booking operations, the customer is generally the controller and {config.company_name} acts as processor to provide the service. This DPA does not change situations where the operator processes data for its own independent purposes, such as account security or billing administration.</p>
      </Section>
      <Section title="2. Processing instructions and categories">
        <p>Processing covers message delivery, conversation handling, booking administration, support and security activities required by the service. Data may include contact identifiers, message content, booking details, language preferences and associated technical metadata. The customer instructs EVA AI through the product configuration and lawful use of the service.</p>
      </Section>
      <Section title="3. Processor commitments">
        <List>
          <li>Process controller data only to provide, secure and support the service or as otherwise required by law.</li>
          <li>Limit access to personnel and systems that need it for authorized purposes.</li>
          <li>Maintain reasonable technical and organizational safeguards appropriate to the service.</li>
          <li>Provide reasonable assistance with verified data-subject requests and compliance obligations.</li>
          <li>Inform the customer without undue delay after becoming aware of a personal-data breach affecting controller data, to the extent required by applicable law.</li>
        </List>
      </Section>
      <Section title="4. Security measures">
        <p>Current application controls include tenant-scoped authorization, password hashing, session invalidation/revocation controls, webhook authentication, security and delivery audit records, atomic booking protections, and authenticated encryption for tenant WhatsApp provider access tokens at rest. Transport and infrastructure security additionally rely on the configured hosting, database and provider platforms.</p>
      </Section>
      <Section title="5. Subprocessors">
        <p>Services that may process data for EVA AI include Meta/WhatsApp, MongoDB Atlas, Anthropic and/or OpenAI, Resend and the configured hosting provider (<strong>{config.hosting_provider}</strong>). Stripe processes subscription/payment information for billing. Exact production arrangements and locations must match the operator's live configuration.</p>
        <p>Configured processing-location information: <strong>{config.processing_locations}</strong>.</p>
      </Section>
      <Section title="6. Cross-border processing">
        <p>If personal data is transferred across borders, the parties will rely on the safeguards required by the applicable data-protection law and the relevant provider arrangements. This DPA intentionally does not assert a particular adequacy decision, SCC coverage, regional hosting location or certification unless it is verified for the actual production deployment.</p>
      </Section>
      <Section title="7. Data-subject requests">
        <p>The customer remains responsible for verifying the identity and legal entitlement of its data subjects. EVA AI will provide reasonable assistance using the data and tooling available to the service. No fixed seven-day deletion or thirty-day export SLA is promised by this DPA unless separately agreed.</p>
      </Section>
      <Section title="8. Return and deletion">
        <p>At termination, controller data will be returned or deleted as required by the applicable agreement and law, subject to legitimate retention needs and technically necessary backup lifecycles. Until automated export/deletion tooling is available, requests are coordinated through <strong>{config.contact_email || 'the configured operator contact'}</strong>.</p>
      </Section>
      <Section title="9. Audit and information">
        <p>EVA AI will provide information reasonably necessary to demonstrate the obligations in this DPA and will cooperate with proportionate audit requests, subject to confidentiality, security and third-party restrictions.</p>
      </Section>
      <Section title="10. Term and precedence">
        <p>This DPA applies while EVA AI processes controller data for the customer's active service. If this DPA conflicts with general Terms on processor-specific data-protection duties, this DPA controls for those duties, subject to mandatory law.</p>
      </Section>
    </>
  );
}

const titles = {
  privacy: 'Privacy Policy',
  terms: 'Terms of Service',
  dpa: 'Data Processing Agreement (DPA)',
};

export default function LegalDocument({ document }) {
  const navigate = useNavigate();
  const { config, loading, error } = useLegalConfig();

  if (loading) {
    return <div className="min-h-screen bg-gray-50 flex items-center justify-center text-slate-600">Loading legal information…</div>;
  }
  if (error || !config) {
    return <div className="min-h-screen bg-gray-50 flex items-center justify-center text-red-700">Legal information is temporarily unavailable.</div>;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-6 py-12">
        <Button variant="ghost" onClick={() => navigate('/')} className="mb-8" data-testid="button-back">
          <ArrowLeft className="w-4 h-4 mr-2" /> Back to home
        </Button>
        <div className="bg-white rounded-lg border border-gray-200 p-8">
          <h1 className="text-4xl font-bold mb-6">{titles[document]}</h1>
          <Header config={config} document={document} />
          {document === 'privacy' && <PrivacyContent config={config} />}
          {document === 'terms' && <TermsContent config={config} />}
          {document === 'dpa' && <DpaContent config={config} />}
        </div>
      </div>
    </div>
  );
}
